import { Link } from 'react-router-dom';
import { IconChevronRight, IconFileText, IconHelpCircle, IconLock, IconTrash } from '@tabler/icons-react';
import { ENV } from '@/config/environment';
import { useCopy, supportLink, deleteDataMailto } from '@/lib/settings';
import { ActsLogo } from './ActsLogo';
import { PoweredByCcms } from './PoweredByCcms';

export interface AboutCarpoolCardProps {
  /** App version shown at the bottom (defaults to VITE_APP_VERSION). */
  version?: string;
  /** Where "Delete my data" goes. Defaults to an email to the privacy officer. Pass null to hide the row. */
  deleteHref?: string | null;
}

/**
 * "About Carpool" for the bottom of Account: privacy notice, terms, help, delete my data, the app
 * version, © Acts Church and Powered by CCMS. On phones this replaces the global footer.
 */
export function AboutCarpoolCard({ version = ENV.APP_VERSION, deleteHref }: AboutCarpoolCardProps) {
  const { t, settings } = useCopy();
  const support = supportLink(settings);
  const appName = t('app.name');
  const del = deleteHref === undefined ? deleteDataMailto(settings, appName) : deleteHref;
  const isInternal = (href: string) => href.startsWith('/') || href.startsWith('#');
  const year = new Date().getFullYear();

  return (
    <section className="rides-card rides-sectioncard" aria-labelledby="rides-about-title">
      <div className="head">
        <div style={{ minWidth: 0 }}>
          <h2 id="rides-about-title">About Carpool</h2>
          <p>{t('about.text')}</p>
        </div>
      </div>
      <ul className="rides-linkrows">
        <li>
          <Link to="/privacy"><span className="ic"><IconLock size={20} aria-hidden="true" /></span><span className="lbl">Privacy notice</span><span className="chev"><IconChevronRight size={20} aria-hidden="true" /></span></Link>
        </li>
        <li>
          <Link to="/terms"><span className="ic"><IconFileText size={20} aria-hidden="true" /></span><span className="lbl">Terms of use</span><span className="chev"><IconChevronRight size={20} aria-hidden="true" /></span></Link>
        </li>
        {support && (
          <li>
            <a href={support.href}><span className="ic"><IconHelpCircle size={20} aria-hidden="true" /></span><span className="lbl">Help and support</span><span className="chev"><IconChevronRight size={20} aria-hidden="true" /></span></a>
          </li>
        )}
        {del && (
          <li>
            {isInternal(del) ? (
              <Link to={del}><span className="ic"><IconTrash size={20} aria-hidden="true" /></span><span className="lbl">Delete my data</span><span className="chev"><IconChevronRight size={20} aria-hidden="true" /></span></Link>
            ) : (
              <a href={del}><span className="ic"><IconTrash size={20} aria-hidden="true" /></span><span className="lbl">Delete my data</span><span className="chev"><IconChevronRight size={20} aria-hidden="true" /></span></a>
            )}
          </li>
        )}
      </ul>
      <div className="rides-about-meta">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <ActsLogo height={18} /> <span>© {year} Acts Church{version ? ` · Version ${version}` : ''}</span>
        </span>
        <span className="rides-about-powered"><PoweredByCcms /></span>
      </div>
    </section>
  );
}
